import { FC, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-relay';
import { Apple, EyeOff, GraduationCap, MapPin, SquarePen } from 'lucide-react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Class } from '../../models/Class';
import { getGradeLabel } from '../../businessLogicService/getClassCardData';
import { resolveClassIcon } from '../../businessLogicService/resolveClassIcon';
import { CLASS_ICON_NAMES } from '../ClassIcon/classIcons';
import ClassIconPicker from '../ClassIcon/ClassIconPicker';
import { useSaveClassIcon } from '../../custom-hooks/useSaveClassIcon';
import { useClassUpdatedEvent } from '../../custom-hooks/useClassUpdatedEvent';
import { useClassIcons } from '../../custom-hooks/useClassIcons';
import { useSideNavActive } from '../LayoutWrapper/SideNavLayoutContext';
import { isUserAdmin } from '../../businessLogicService/isUserAdmin';
import { selectSelectedClassIds } from '../../redux/reducers/globalClassFilterSlice';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { Pin } from 'lucide-react';
import { hideClass as hideClassMutation } from '../../graphQLOperations/ClassCategories';
import { ClassCategoriesHideClassMutation } from '../../graphQLOperations/__generated__/ClassCategoriesHideClassMutation.graphql';
import { useAppDispatch } from '../../redux/hooks';
import { noteClassHidden, setClassHidden } from '../../redux/reducers/hiddenClassesSlice';
import ClassesRosterClassFormDialog from './ClassesRosterDialog/ClassesRosterClassFormDialog';

interface ClassesRosterLeftSideProps {
  selectedClass: Class | null;
  setSelectedClass: (c: Class) => void;
  userOwnerOfClass?: boolean;
}

const ClassesRosterLeftSide: FC<ClassesRosterLeftSideProps> = ({
  selectedClass,
  setSelectedClass,
  userOwnerOfClass,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [classModal, setClassModal] = useState<{ display: boolean; isEdit: boolean }>({
    display: false,
    isEdit: false,
  });
  const [hideCandidate, setHideCandidate] = useState<Class | null>(null);
  const sideNavActive = useSideNavActive();
  const storedIcons = useClassIcons();
  const userRedux = useAppSelector(selectUser);
  const globalClassIds = useAppSelector(selectSelectedClassIds) ?? [];
  const saveClassIcon = useSaveClassIcon();
  const reportClassUpdated = useClassUpdatedEvent();
  const classId = selectedClass?.id ?? '';
  const icon = classId ? resolveClassIcon(classId, storedIcons, CLASS_ICON_NAMES) : null;
  const workingIn =
    !isUserAdmin(userRedux) && Boolean(classId) && (!globalClassIds.length || globalClassIds.includes(classId));

  const saveIcon = (iconName: string) => {
    saveClassIcon(classId, iconName);

    if (iconName !== icon) {
      reportClassUpdated({
        classId,
        fieldsChanged: ['icon'],
        entryPoint: 'class_details_page',
        icon: iconName,
      });
    }
  };
  const toast = useRef<Toast | null>(null);
  const [commitHide, hiding] = useMutation<ClassCategoriesHideClassMutation>(hideClassMutation);
  const confirmHide = () => {
    if (!hideCandidate) return;
    const classId = hideCandidate.id;
    const className = hideCandidate.name;
    commitHide({
      variables: { input: { classId } },
      onCompleted: response => {
        const error = response.hideClass?.errors?.[0];
        if (error && 'message' in error) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: error.message });
          return;
        }
        dispatch(setClassHidden({ classId, hidden: true }));
        dispatch(noteClassHidden(className ?? ''));
        navigate('/classes-rosters');
      },
      onError: () => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not hide the class.' }),
    });
  };

  const hideDialogFooter = (
    <div className="flex justify-content-end gap-md w-full">
      <Button label="Cancel" outlined rounded onClick={() => setHideCandidate(null)} disabled={hiding} />
      <Button label="Hide class" rounded onClick={confirmHide} loading={hiding} />
    </div>
  );

  if (!selectedClass || !selectedClass.name) {
    return null;
  }

  return (
    <>
      <div className="flex flex-column w-full nc_sidebar">
        <Toast ref={toast} />
        {classModal.display && (
          <ClassesRosterClassFormDialog
            classList={[]}
            setClassList={() => undefined}
            isEdit
            visible={classModal.display}
            setVisible={setClassModal}
            initialEditValues={selectedClass}
            toast={toast}
            setSelectedClass={setSelectedClass}
            entryPoint="class_details_page"
          />
        )}
        <Card className={`nc_class-info-card ${sideNavActive ? 'nc_class-info-card--top' : ''}`}>
          {sideNavActive && workingIn && (
            <span className="nc_class-card__working-in">
              <Pin size={12} aria-hidden="true" />
              Working in
            </span>
          )}
          <div className={sideNavActive ? 'nc_class-info-top' : ''}>
            {sideNavActive && <ClassIconPicker value={icon} onChange={saveIcon} label="Change class icon" />}
            <div className="flex justify-content-between align-items-start gap-sm">
              <div className="flex flex-column nc_class-info-rows">
                <span className="flex align-items-start tw-font-bold nc_class-info-row">
                  <GraduationCap
                    size={20}
                    color="#9C9B9B"
                    aria-hidden
                    className="flex-shrink-0"
                    style={{ width: 20, height: 20 }}
                  />
                  {getGradeLabel(selectedClass?.gradeLevels)}
                </span>
                <span className="flex align-items-center tw-font-bold nc_class-info-row">
                  <MapPin size={20} color="#9C9B9B" aria-hidden />
                  {selectedClass.site?.name}
                </span>
                <span className="flex align-items-center tw-font-bold nc_class-info-row">
                  <Apple size={20} color="#9C9B9B" aria-hidden />
                  {selectedClass?.owner?.firstName || ''} {selectedClass?.owner?.lastName || ''}
                </span>
              </div>
            </div>
            {userOwnerOfClass && (
              <div
                className={`flex flex-column align-items-center gap-sm ${sideNavActive ? '' : 'w-full'}`}
                style={{ marginTop: sideNavActive ? '0' : '3rem' }}
              >
                <Button
                  label="Edit class details"
                  icon={<SquarePen size={18} aria-hidden />}
                  outlined
                  rounded
                  disabled={Boolean(selectedClass?.externalSync)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                    width: sideNavActive ? 'auto' : '100%',
                  }}
                  onClick={() => setClassModal({ isEdit: true, display: true })}
                  id="editClassButton"
                  className={sideNavActive ? '' : 'w-full'}
                />
                {selectedClass?.externalSync && (
                  <small className="w-full text-start tw-bg-neutral-100 tw-text-[#393939] tw-rounded-lg tw-p-1 tw-font-bold">
                    Class editing and rostering are managed in Clever.
                  </small>
                )}
                <Button
                  label="Hide class"
                  icon={<EyeOff size={18} aria-hidden />}
                  text
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                    width: sideNavActive ? 'auto' : '100%',
                  }}
                  onClick={() => setHideCandidate(selectedClass)}
                  id="hideClassButton"
                  className={`nc_hide-class-btn ${sideNavActive ? '' : 'w-full'}`}
                />
              </div>
            )}
          </div>
        </Card>
      </div>
      <Dialog
        header={`Hide ${hideCandidate?.name ?? ''}?`}
        visible={Boolean(hideCandidate)}
        footer={hideDialogFooter}
        onHide={() => setHideCandidate(null)}
        className="nc_hide-dialog"
        style={{ width: '32rem' }}
        dismissableMask
        closable={false}
      >
        <p className="m-0">
          Hidden classes will no longer appear in your Class selection dropdowns.{' '}
          <b>You can un-hide them from the Hidden classes section on the class homepage.</b>
        </p>
      </Dialog>
    </>
  );
};

export default ClassesRosterLeftSide;
